import { forwardRef } from 'react';
import { Loader2 } from 'lucide-react';

import { cn } from '@/shared/lib/cn';

type Variant = 'primary' | 'secondary' | 'ghost' | 'danger' | 'dangerOutline' | 'outline' | 'onMedia';
type Size = 'sm' | 'md' | 'lg' | 'icon' | 'icon-sm';

const VARIANTS: Record<Variant, string> = {
  // The accent is reserved for the one primary action on a view.
  primary: 'bg-accent text-accent-fg hover:bg-accent-hover active:bg-accent-hover shadow-sm',
  secondary: 'bg-surface-raised text-fg border border-border hover:bg-surface-hover hover:border-border-strong',
  outline: 'bg-transparent text-fg border border-border hover:bg-surface-hover hover:border-border-strong',
  ghost: 'bg-transparent text-fg-muted hover:bg-surface-hover hover:text-fg',
  danger: 'bg-danger text-white hover:opacity-90',
  /*
   * A destructive action that is not the point of the view ("Ngắt kết nối" next to "Lưu"). A
   * filled red button there out-shouts the primary action and invites the wrong click; the
   * confirmation dialog behind it carries the filled `danger` button instead.
   */
  dangerOutline: 'bg-transparent text-danger border border-danger/40 hover:bg-danger-subtle hover:border-danger/60',
  /*
   * For controls that sit on top of a photo or a video frame.
   *
   * The surface variants are page treatments: `secondary` is near-black in the dark theme and
   * white in the light one, so over arbitrary media its contrast is decided by the image, not
   * by the design — a black chip vanishes into a dark thumbnail, a white one into a bright
   * sky. A fixed dark scrim with white content reads on both, in either theme.
   */
  onMedia:
    'bg-black/55 text-white border border-white/25 backdrop-blur-sm shadow-sm hover:bg-black/75 hover:border-white/40',
};

const SIZES: Record<Size, string> = {
  sm: 'h-8 px-3 text-sm gap-1.5',
  md: 'h-9 px-4 text-base gap-2',
  lg: 'h-11 px-5 text-md gap-2',
  icon: 'h-9 w-9',
  'icon-sm': 'h-8 w-8',
};

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant;
  size?: Size;
  loading?: boolean;
  icon?: React.ReactNode;
  iconRight?: React.ReactNode;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { className, variant = 'secondary', size = 'md', loading, icon, iconRight, children, disabled, type = 'button', ...props },
  ref,
) {
  const isIconOnly = size === 'icon' || size === 'icon-sm';
  return (
    <button
      ref={ref}
      type={type}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      className={cn(
        'inline-flex shrink-0 items-center justify-center rounded font-medium',
        'transition-colors duration-150 cursor-pointer select-none whitespace-nowrap',
        'disabled:pointer-events-none disabled:opacity-50',
        VARIANTS[variant],
        SIZES[size],
        className,
      )}
      {...props}
    >
      {loading ? (
        <Loader2 className={cn('animate-spin', isIconOnly ? 'h-4 w-4' : 'h-3.5 w-3.5')} aria-hidden />
      ) : (
        icon
      )}
      {/*
        An icon-only button still renders its children when no `icon` prop was given.
        `{!isIconOnly && children}` silently threw them away, so the very common
        `<Button size="icon-sm" aria-label="…"><X /></Button>` produced an empty coloured
        square — 31 of them across the app, including every dialog's close button. Dropping
        content is never the safer default: a stray label is visible and gets fixed, an
        invisible control is reported as "the icons are broken".
      */}
      {!isIconOnly ? children : icon ? null : children}
      {!loading && iconRight}
    </button>
  );
});

/**
 * A link that looks like a button. Separate from `Button` on purpose: nesting an <a> inside a
 * <button> is invalid HTML and breaks keyboard and screen-reader behaviour.
 */
export function buttonClasses(variant: Variant = 'secondary', size: Size = 'md', className?: string) {
  return cn(
    'inline-flex shrink-0 items-center justify-center rounded font-medium no-underline',
    'transition-colors duration-150 cursor-pointer select-none whitespace-nowrap',
    VARIANTS[variant],
    SIZES[size],
    className,
  );
}
